import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { 
  Building2, Plus, Edit, Trash2, Power, PowerOff, Search, RefreshCw,
  Users, Upload, Bot, ChevronLeft, ChevronRight, Eye, ExternalLink,
  Calendar, CheckCircle, XCircle, AlertTriangle, MoreVertical, Shield
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { formatApiError } from '../lib/utils';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const SuperAdminCompanies = () => {
  const navigate = useNavigate();
  const { setImpersonatedTenant } = useAuth();
  
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  
  // Modals
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  
  // Create form
  const [formData, setFormData] = useState({
    companyName: '',
    adminName: '',
    adminEmail: '',
    maxUserCount: 10
  });

  // V26: Edit user limit
  const [editLimitCompany, setEditLimitCompany] = useState(null);
  const [editLimitValue, setEditLimitValue] = useState(10);
  const [editLimitLoading, setEditLimitLoading] = useState(false);

  const handleUpdateLimit = async () => {
    if (!editLimitCompany) return;
    setEditLimitLoading(true);
    try {
      const response = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/super-admin/companies/${editLimitCompany.companyId}/user-limit`,
        { maxUserCount: editLimitValue },
        { 
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      const data = response.data;
      if (data.warning) {
        toast.warning(data.warning);
      } else {
        toast.success(`User limit updated to ${editLimitValue}`);
      }
      setEditLimitCompany(null);
      loadCompanies();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to update user limit'));
    } finally {
      setEditLimitLoading(false);
    }
  };

  const loadCompanies = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: pageSize,
        skip: page * pageSize
      });
      
      const response = await axios.get(`${BACKEND_URL}/api/super-admin/companies?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      let filteredCompanies = response.data.companies || [];
      
      // Client-side filtering
      if (searchTerm) {
        filteredCompanies = filteredCompanies.filter(c => 
          c.companyName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.companyId?.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      if (statusFilter) {
        filteredCompanies = filteredCompanies.filter(c => c.status === statusFilter);
      }
      
      setCompanies(filteredCompanies);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load companies:', error);
      toast.error('Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, searchTerm, statusFilter]);

  useEffect(() => {
    loadCompanies();
  }, [loadCompanies]);

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    if (!formData.companyName || !formData.adminName || !formData.adminEmail) {
      toast.error('Please fill in all fields');
      return;
    }
    
    setActionLoading(true);
    try {
      const payload = {
        ...formData,
        maxUserCount: parseInt(formData.maxUserCount, 10) || 10
      };
      const response = await axios.post(`${BACKEND_URL}/api/super-admin/create-company`, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      toast.success(response.data.message || 'Company created successfully');
      setShowCreateModal(false);
      setFormData({ companyName: '', adminName: '', adminEmail: '', maxUserCount: 10 });
      loadCompanies();
    } catch (error) {
      toast.error(formatApiError(error, 'Failed to create company'));
    } finally {
      setActionLoading(false);
    }
  };

  const handleToggleStatus = async (company) => {
    const newStatus = company.status === 'active' ? 'inactive' : 'active';
    setActionLoading(true);
    try {
      await axios.put(
        `${BACKEND_URL}/api/super-admin/companies/${company.tenantId}/status?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }}
      );
      toast.success(`Company ${newStatus === 'active' ? 'activated' : 'deactivated'}`);
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update status');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteCompany = async () => {
    if (!selectedCompany) return;
    setActionLoading(true);
    try {
      await axios.delete(`${BACKEND_URL}/api/super-admin/companies/${selectedCompany.companyId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      toast.success('Company deleted successfully');
      setShowDeleteModal(false);
      setSelectedCompany(null);
      loadCompanies();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete company');
    } finally {
      setActionLoading(false);
    }
  };

  const handleImpersonate = (company) => {
    const tenantData = {
      tenantId: company.tenantId,
      companyId: company.companyId,
      companyName: company.companyName || company.name
    };
    
    // Store impersonation state
    localStorage.setItem('impersonatedTenant', JSON.stringify(tenantData));
    
    toast.success(`Now viewing as ${tenantData.companyName}`);
    
    // Navigate with a slight delay to ensure localStorage is set
    setTimeout(() => {
      navigate('/dashboard');
    }, 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        year: 'numeric', month: 'short', day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="space-y-6" data-testid="superadmin-companies-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
            <Building2 className="h-7 w-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Company Management</h1>
            <p className="text-sm text-slate-500">Manage all companies and tenants on the platform</p>
          </div>
        </div>
        <Button onClick={() => setShowCreateModal(true)} className="bg-indigo-600 hover:bg-indigo-700" data-testid="create-company-btn">
          <Plus className="h-4 w-4 mr-2" />
          Create Company
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Search companies..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-input"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-slate-200 dark:border-slate-700 rounded-lg text-sm bg-white dark:bg-slate-800"
              data-testid="status-filter"
            >
              <option value="">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <Button variant="outline" onClick={loadCompanies} data-testid="refresh-btn">
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Companies Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span>Companies ({total})</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600"></div>
            </div>
          ) : companies.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Building2 className="h-12 w-12 mx-auto mb-3 opacity-30" />
              <p>No companies found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200 dark:border-slate-700">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Company</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Company ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Status</th>
                    <th className="text-center py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">User Limit</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Created</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-slate-600 dark:text-slate-300">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-700">
                  {companies.map((company) => (
                    <tr key={company.tenantId} className="hover:bg-slate-50 dark:hover:bg-slate-800/50" data-testid={`company-row-${company.companyId}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-100 to-purple-100 dark:from-indigo-900/30 dark:to-purple-900/30 flex items-center justify-center">
                            <Building2 className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900 dark:text-white">{company.companyName || company.name}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <code className="text-xs bg-slate-100 dark:bg-slate-700 px-2 py-1 rounded">{company.companyId}</code>
                      </td>
                      <td className="py-3 px-4">
                        <Badge variant={company.status === 'active' ? 'default' : 'secondary'} className={company.status === 'active' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400' : 'bg-slate-100 text-slate-600'}>
                          {company.status === 'active' ? <CheckCircle className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                          {company.status}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-center" data-testid={`user-limit-${company.companyId}`}>
                        <div className="flex items-center justify-center gap-2">
                          <Users className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                            {company.maxUserCount || 'N/A'}
                          </span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setEditLimitCompany(company);
                              setEditLimitValue(company.maxUserCount || 10);
                            }}
                            data-testid={`edit-limit-btn-${company.companyId}`}
                          >
                            <Edit className="h-3 w-3 text-slate-400 hover:text-indigo-600" />
                          </Button>
                        </div>
                      </td>
                      <td className="py-3 px-4">
                        <span className="text-sm text-slate-500">{formatDate(company.createdAt)}</span>
                      </td>
                      <td className="py-3 px-4">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleImpersonate(company)}
                            className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                            data-testid={`impersonate-btn-${company.companyId}`}
                          >
                            <Eye className="h-4 w-4 mr-1" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleToggleStatus(company)}
                            className={company.status === 'active' ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}
                          >
                            {company.status === 'active' ? <PowerOff className="h-4 w-4" /> : <Power className="h-4 w-4" />}
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => { setSelectedCompany(company); setShowDeleteModal(true); }}
                            className="text-red-600 hover:bg-red-50"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between pt-4 border-t border-slate-200 dark:border-slate-700 mt-4">
              <p className="text-sm text-slate-500">
                Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, total)} of {total}
              </p>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm text-slate-600">Page {page + 1} of {totalPages}</span>
                <Button variant="outline" size="sm" onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Company Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowCreateModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="create-company-modal">
            <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
              <Building2 className="h-5 w-5 text-indigo-600" />
              Create New Company
            </h2>
            <form onSubmit={handleCreateCompany} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Company Name *</label>
                <Input
                  value={formData.companyName}
                  onChange={(e) => setFormData({...formData, companyName: e.target.value})}
                  placeholder="e.g., Acme Retail"
                  data-testid="company-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Admin Name *</label>
                <Input
                  value={formData.adminName}
                  onChange={(e) => setFormData({...formData, adminName: e.target.value})}
                  placeholder="e.g., John Smith"
                  data-testid="admin-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Admin Email *</label>
                <Input
                  type="email"
                  value={formData.adminEmail}
                  onChange={(e) => setFormData({...formData, adminEmail: e.target.value})}
                  placeholder="e.g., admin@company.com"
                  data-testid="admin-email-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Maximum User Count *</label>
                <Input
                  type="number"
                  min="1"
                  max="10000"
                  value={formData.maxUserCount}
                  onChange={(e) => setFormData({...formData, maxUserCount: e.target.value === '' ? '' : parseInt(e.target.value, 10) || ''})}
                  placeholder="e.g., 10"
                  data-testid="modal-max-user-count-input"
                />
                <p className="text-xs text-slate-500 mt-1">Max active users (1–10,000). Admin counts as 1.</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  Default password will be: <code className="font-mono">Welcome@123</code>
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowCreateModal(false)}>Cancel</Button>
                <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" disabled={actionLoading} data-testid="submit-create-btn">
                  {actionLoading ? 'Creating...' : 'Create Company'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDeleteModal(false)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="delete-company-modal">
            <div className="text-center">
              <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="h-8 w-8 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-2">Delete Company</h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Are you sure you want to delete <strong>{selectedCompany.companyName || selectedCompany.name}</strong>?
              </p>
              <p className="text-sm text-red-600 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 mb-4">
                This will permanently delete all users, uploads, and data associated with this company.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Cancel</Button>
                <Button onClick={handleDeleteCompany} className="bg-red-600 hover:bg-red-700" disabled={actionLoading} data-testid="confirm-delete-btn">
                  {actionLoading ? 'Deleting...' : 'Delete Company'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Edit User Limit Modal */}
      {editLimitCompany && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setEditLimitCompany(null)}>
          <div className="bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="edit-limit-modal">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-4">
              Update User Limit — {editLimitCompany.companyName || editLimitCompany.name}
            </h2>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">Current Maximum</label>
                <p className="text-lg font-semibold text-slate-900 dark:text-white">{editLimitCompany.maxUserCount || 'N/A'}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">New Maximum User Count</label>
                <Input
                  type="number"
                  min="1"
                  max="10000"
                  value={editLimitValue}
                  onChange={(e) => setEditLimitValue(parseInt(e.target.value, 10) || 1)}
                  data-testid="edit-limit-input"
                />
              </div>
              <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  <AlertTriangle className="h-4 w-4 inline mr-1" />
                  If the new limit is below the current active user count, a 15-day grace period will be applied.
                </p>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setEditLimitCompany(null)}>Cancel</Button>
                <Button 
                  onClick={handleUpdateLimit} 
                  className="bg-indigo-600 hover:bg-indigo-700" 
                  disabled={editLimitLoading}
                  data-testid="confirm-edit-limit-btn"
                >
                  {editLimitLoading ? 'Updating...' : 'Update Limit'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SuperAdminCompanies;
